import type { CSSProperties } from 'react';
import { cn } from '@/lib/cn';
import '@/kit/hottr-image.css';

/**
 * Six nightlife scene archetypes — usable two ways:
 *
 * 1. WITHOUT a photo: pure CSS scene (atmospheric monochrome with selective
 *    pink accent). The original behaviour, used for cards we don't have a
 *    photo for and for landing-only fallbacks.
 *
 * 2. WITH a photoUrl: the photo sits as the base layer (grayscale, contrast,
 *    deepened) and the per-scene pink/light accent overlays on top via
 *    mix-blend-mode. Vignette + grain finish the composite.
 *
 * Either way, pink stays as selective signal — never as decoration.
 */
export type Scene =
  | 'club_room'        // pink corner glow upper-right — like a phone screen / window light
  | 'flash'            // hard white flash spot — a paparazzi moment
  | 'mirror'           // pink horizon seam bisecting — mirror or shoreline
  | 'alley_neon'       // pink vertical sliver on right edge — neon sign
  | 'haze'             // pink top rim + smoke from below
  | 'backstage';       // pink edge light from right, deep left shadow

interface EditorialImageProps {
  scene: Scene;
  /** Optional Unsplash hot-link or self-hosted photo URL. */
  photoUrl?: string;
  /** Stable seed for per-card variance. */
  seed?: number;
  /** Aspect ratio class — defaults to 4/5 (editorial portrait). */
  aspect?: string;
  /**
   * Black-and-white editorial-cover treatment. Used on landings where the
   * featured profile photo needs to feel like a magazine cover, not the
   * matte-colour scroll on the in-app feed.
   */
  mono?: boolean;
  className?: string;
}

const variance = (seed: number, range: number) =>
  ((seed * 9301 + 49297) % 233280) / 233280 * range;

/**
 * Photo-less base atmosphere: a monochrome ink/grey gradient with a per-scene
 * pink accent baked in. Used when no photoUrl is provided.
 */
const SCENES_NO_PHOTO: Record<Scene, (seed: number) => CSSProperties> = {
  club_room: (seed) => ({
    background: `
      radial-gradient(120% 90% at ${15 + variance(seed, 20)}% ${10 + variance(seed, 15)}%,
        rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.03) 28%, transparent 55%),
      radial-gradient(60% 50% at 78% 70%, rgba(255,12,182,0.06) 0%, transparent 50%),
      linear-gradient(135deg, #14151a 0%, #0a0a0d 50%, #050608 100%)
    `,
  }),
  flash: (seed) => ({
    background: `
      radial-gradient(45% 35% at ${48 + variance(seed, 8)}% ${30 + variance(seed, 12)}%,
        rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.08) 18%, transparent 42%),
      radial-gradient(80% 60% at 50% 30%, rgba(35,35,40,1) 0%, rgba(8,8,11,1) 80%)
    `,
  }),
  mirror: (seed) => ({
    background: `
      linear-gradient(180deg,
        rgba(40,40,46,1) 0%,
        rgba(30,30,36,1) 38%,
        rgba(255,12,182,0.6) 49%,
        rgba(255,12,182,0.6) 50%,
        rgba(8,8,11,1) 52%,
        rgba(5,6,8,1) 100%
      )
    `,
    transform: `scaleX(${seed % 2 === 0 ? 1 : -1})`,
  }),
  alley_neon: (seed) => ({
    background: `
      linear-gradient(90deg,
        rgba(5,6,8,1) 0%,
        rgba(10,11,14,1) 60%,
        rgba(20,18,24,1) 80%,
        rgba(255,12,182,0.45) 96%,
        rgba(255,12,182,0.85) 100%
      ),
      radial-gradient(70% 30% at 50% 100%, rgba(255,255,255,0.08) 0%, transparent 60%)
    `,
    backgroundBlendMode: 'screen, normal',
    transform: `scaleX(${seed % 3 === 0 ? -1 : 1})`,
  }),
  haze: (seed) => ({
    background: `
      linear-gradient(0deg,
        rgba(60,60,66,0.55) 0%,
        rgba(30,30,36,0.40) 30%,
        rgba(12,12,16,0.10) 65%,
        transparent 90%
      ),
      radial-gradient(80% 20% at ${50 + variance(seed, 15)}% 12%,
        rgba(255,12,182,0.18) 0%, transparent 70%),
      linear-gradient(180deg, #0a0a0e 0%, #050608 100%)
    `,
  }),
  backstage: (seed) => ({
    background: `
      linear-gradient(${75 + variance(seed, 20)}deg,
        rgba(5,6,8,1) 0%,
        rgba(8,8,11,1) 50%,
        rgba(40,40,46,1) 88%,
        rgba(80,80,86,1) 100%
      )
    `,
  }),
};

/** Scene fills stay product-local. Photographic grade lives in kit/hottr-image.css. */

export const EditorialImage = ({
  scene,
  photoUrl,
  seed = 1,
  aspect = 'aspect-[4/5]',
  mono = false,
  className,
}: EditorialImageProps) => {
  const hasPhoto = Boolean(photoUrl);

  return (
    <div
      className={cn('relative isolate w-full overflow-hidden bg-night', aspect, className)}
      style={hasPhoto ? undefined : SCENES_NO_PHOTO[scene](seed)}
      aria-hidden
    >
      {/* PHOTO — colour matte by default, B&W when mono */}
      {hasPhoto && (
        <img
          src={photoUrl}
          alt=""
          loading="lazy"
          decoding="async"
          className={cn(
            'hottr-matte-editorial absolute inset-0 h-full w-full object-cover object-center',
            mono && 'hottr-matte-editorial--mono',
          )}
        />
      )}

      {hasPhoto && <span className="hottr-matte-editorial__scrim" aria-hidden />}
      <span className="hottr-matte-editorial__vignette" aria-hidden />
      <span className="hottr-matte-editorial__grain" aria-hidden />
    </div>
  );
};
